import clsx from 'clsx';
import { useMemo, useRef, useState } from 'react';
import { useLeads, useStages, useUpdateLead } from '../api/queries';
import { daysSince, fmtILS, isOverdue, STALE_DAYS } from '../utils/format';
import type { Lead, Stage } from '@figureit/shared';
import { useIsMobile } from '../hooks/useViewport';
import { WonLostPrompt } from './WonLostPrompt';

export function KanbanView({ onSelectLead }: { onSelectLead: (id: string) => void }) {
  const stagesQ = useStages();
  const leadsQ = useLeads();
  const update = useUpdateLead();
  const isMobile = useIsMobile();

  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [pendingTerminal, setPendingTerminal] = useState<{
    leadId: string;
    targetStage: Stage;
  } | null>(null);

  const stages = useMemo(
    () => (stagesQ.data?.items ?? []).slice().sort((a, b) => a.order - b.order),
    [stagesQ.data],
  );
  const leads = leadsQ.data?.items ?? [];

  // Shared by desktop drag-drop and the phone "Move to…" picker.
  const moveLead = (leadId: string, stage: Stage) => {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || lead.stageId === stage.id) return;
    if (stage.isWon || stage.isLost) {
      // Open the prompt; it owns the actual mutation once a reason is picked.
      setPendingTerminal({ leadId, targetStage: stage });
      return;
    }
    update.mutate({ id: leadId, stageId: stage.id });
  };

  const handleDrop = (stage: Stage) => {
    if (!dragId) return;
    moveLead(dragId, stage);
  };

  // Phone: columns are a snap-scrolling carousel; the chip row jumps to a
  // column and highlights the one currently in view.
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const colRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [activeStageId, setActiveStageId] = useState<string | null>(null);
  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el || !isMobile) return;
    const center = el.scrollLeft + el.clientWidth / 2;
    let best: string | null = null;
    let bestDist = Infinity;
    for (const s of stages) {
      const col = colRefs.current[s.id];
      if (!col) continue;
      const mid = col.offsetLeft + col.offsetWidth / 2;
      const d = Math.abs(mid - center);
      if (d < bestDist) {
        bestDist = d;
        best = s.id;
      }
    }
    if (best !== activeStageId) setActiveStageId(best);
  };
  const jumpTo = (id: string) => {
    colRefs.current[id]?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  };
  const currentStageId = activeStageId ?? stages[0]?.id ?? null;

  return (
    <div className="h-full flex flex-col">
      {isMobile && stages.length > 0 && (
        <div className="flex gap-1.5 px-3 pt-2 pb-1 overflow-x-auto no-scrollbar flex-shrink-0">
          {stages.map((s) => {
            const n = leads.filter((l) => l.stageId === s.id).length;
            const active = s.id === currentStageId;
            return (
              <button
                key={s.id}
                onClick={() => jumpTo(s.id)}
                className={clsx(
                  'flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium flex-shrink-0 min-h-[36px]',
                  active ? 'border-accent bg-accent/15 text-text' : 'border-border text-text2',
                )}
              >
                <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                {s.name}
                <span className="text-text3">{n}</span>
              </button>
            );
          })}
        </div>
      )}
      <div
        ref={scrollerRef}
        onScroll={onScroll}
        className="flex gap-3 px-3 md:px-6 py-2 md:py-4 overflow-x-auto overscroll-x-contain flex-1 min-h-0 items-stretch md:items-start snap-x snap-mandatory md:snap-none"
      >
        {stages.map((stage) => {
          const stageLeads = leads.filter((l) => l.stageId === stage.id);
          const yearlySum = stageLeads.reduce((s, l) => s + (Number(l.dealValueYearly) || 0), 0);
          // Weighted forecast for this column = sum * probability%.
          const forecast = (yearlySum * (stage.probability ?? 0)) / 100;
          return (
            <div
              key={stage.id}
              ref={(el) => {
                colRefs.current[stage.id] = el;
              }}
              className="w-[calc(100vw-2.5rem)] sm:w-[320px] md:w-auto md:min-w-[280px] md:max-w-[320px] md:flex-[0_0_280px] flex-shrink-0 snap-center bg-surface rounded-xl flex flex-col max-h-full"
            >
              <div className="p-3 border-b border-border flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full inline-block"
                      style={{ background: stage.color }}
                    />
                    {stage.name}
                    <span className="text-[11px] text-text3 bg-surface2 px-1.5 py-0.5 rounded-md">
                      {stageLeads.length}
                    </span>
                  </div>
                  <span className="text-[11px] text-text3" title="Stage probability">
                    {stage.probability}%
                  </span>
                </div>
                {yearlySum > 0 && (
                  <div className="flex items-center justify-between text-[11px] text-text3 mt-1">
                    <span>{fmtILS(yearlySum)}/yr</span>
                    {forecast > 0 && stage.probability > 0 && stage.probability < 100 && (
                      <span className="text-accent2" title="Probability-weighted forecast">
                        ≈ {fmtILS(forecast)}/yr
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div
                className={clsx(
                  'p-2 overflow-y-auto overscroll-contain flex-1 min-h-[60px] transition-colors',
                  dragOver === stage.id && 'bg-accent/10',
                )}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(stage.id);
                }}
                onDragLeave={() => setDragOver(null)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(null);
                  handleDrop(stage);
                }}
              >
                {stageLeads.length === 0 && (
                  <div className="text-center text-text3 text-xs italic py-6">
                    {isMobile ? 'No leads in this stage' : 'Drop leads here'}
                  </div>
                )}
                {stageLeads.map((lead) => (
                  <KanbanCard
                    key={lead.id}
                    lead={lead}
                    stage={stage}
                    stages={stages}
                    isMobile={isMobile}
                    dragging={dragId === lead.id}
                    onDragStart={() => setDragId(lead.id)}
                    onDragEnd={() => {
                      setDragId(null);
                      setDragOver(null);
                    }}
                    onOpen={() => onSelectLead(lead.id)}
                    onMove={(s) => moveLead(lead.id, s)}
                    busy={update.isPending}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {pendingTerminal && (
        <WonLostPrompt
          leadId={pendingTerminal.leadId}
          targetStage={pendingTerminal.targetStage}
          onClose={() => setPendingTerminal(null)}
          onDone={() => setPendingTerminal(null)}
        />
      )}
    </div>
  );
}

function KanbanCard({
  lead,
  stage,
  stages,
  isMobile,
  dragging,
  onDragStart,
  onDragEnd,
  onOpen,
  onMove,
  busy,
}: {
  lead: Lead;
  stage: Stage;
  stages: Stage[];
  isMobile: boolean;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onOpen: () => void;
  onMove: (s: Stage) => void;
  busy: boolean;
}) {
  const stuckDays = daysSince(lead.stageEnteredAt);
  const slaBreached = !!stage.slaDays && stuckDays > stage.slaDays;
  return (
    <div
      className={clsx(
        'bg-surface2 border rounded-md p-3 mb-2 transition-all',
        !isMobile && 'cursor-grab hover:-translate-y-px',
        slaBreached ? 'border-red/50 hover:border-red' : 'border-border hover:border-accent',
        dragging && 'opacity-50',
      )}
      draggable={!isMobile}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-semibold truncate">{lead.company}</div>
        <span className={`badge badge-${(lead.priority || 'B').toLowerCase()}`}>
          {lead.priority || 'B'}
        </span>
      </div>
      <div className="text-xs text-text2 mb-1.5 truncate">
        {lead.contactName}
        {lead.contactTitle ? ` · ${lead.contactTitle}` : ''}
      </div>
      <div className="flex items-center justify-between text-[11px] gap-2">
        <span className="text-green font-semibold truncate">
          {lead.nreValue ? `NRE ${fmtILS(lead.nreValue)}` : ''}
          {lead.nreValue && lead.dealValueYearly ? ' · ' : ''}
          {lead.dealValueYearly ? `${fmtILS(lead.dealValueYearly)}/yr` : ''}
          {!lead.nreValue && !lead.dealValueYearly ? '—' : ''}
        </span>
        <span
          className={clsx(
            'text-[10px] px-1.5 py-0.5 rounded',
            slaBreached ? 'bg-red/15 text-red' : 'text-text3',
          )}
          title={slaBreached ? `Past stage SLA of ${stage.slaDays}d` : 'Time in this stage'}
        >
          {stuckDays}d
        </span>
      </div>
      {lead.nextAction && (
        <div className="text-[11px] text-text3 mt-1 truncate">{lead.nextAction}</div>
      )}
      {daysSince(lead.lastActivityAt) >= STALE_DAYS && (
        <div className="text-orange font-semibold text-[10px] mt-1">
          ⚠ {daysSince(lead.lastActivityAt)}d since last activity
        </div>
      )}
      {isOverdue(lead.nextActionDate) && (
        <div className="text-red font-semibold text-[10px] mt-1">⏰ Overdue action</div>
      )}
      {isMobile && (
        // Touch has no HTML5 drag-drop, so moving between stages is a picker.
        <div className="mt-2 pt-2 border-t border-border" onClick={(e) => e.stopPropagation()}>
          <select
            value={stage.id}
            disabled={busy}
            onChange={(e) => {
              const next = stages.find((s) => s.id === e.target.value);
              if (next) onMove(next);
            }}
            className="!min-h-[40px] !py-1.5 !text-sm text-text2"
            aria-label="Move to stage"
          >
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.id === stage.id ? `Stage: ${s.name}` : `Move to ${s.name}`}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
